import { DragDropModule } from '@angular/cdk/drag-drop';
import { CurrencyPipe, DatePipe, DecimalPipe, TitleCasePipe, UpperCasePipe } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
// Angular Material Module imports
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { UrlSerializer } from '@angular/router';
import { PktrAppMenuModule, PktrAppMenuService } from '@prospektr/pktr-app-menu';
import { IConfig, NgxMaskModule } from 'ngx-mask';
import { Observable } from 'rxjs';
import { CoreModule } from './_core/core.module';
import { TrialAccountValidatorGuard } from './_shared/guard/trial-account-validator.guard';
import { DurationPipe } from './_shared/pipe/duration.pipe';
import { NumberFormatterPipe } from './_shared/pipe/number.pipe';
import { AppInitializer } from './_shared/resolver/app.initializer';
import { DealAnalysisService } from './_shared/service/deal-analysis.service';
import { GTMService } from './_shared/service/gtm.service';
import { WindowService } from './_shared/service/window.service';
import { SharedModule } from './_shared/shared.module';
import { AgentLoSignupModule } from './agent-lo-signup/agent-lo-signup.module';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AppService } from './app.service';
import { AutomatedRecommendationComponent } from './automated-recommendation/automated-recommendation.component';
import { CampaignsModule } from './campaigns/campaigns.module';
import { ClientDashboardModule } from "./client-dashboard/client-dashboard.module";
import { DealAnalysisModule } from './deal-analysis/deal-analysis.module';
import { DiscoverClientsComponent } from './discover-clients/discover-clients.component';
import { EquityAnalysisComponent } from './equity-analysis/equity-analysis.component';
import { FollowedPropertiesModule } from './followed-properties/followed-properties.module';
import { MarketingComponent } from './marketing/marketing.component';
import { ContainerPageComponent } from './modal-container/component/container-page/container-page.component';
import { StatusInfoComponent } from './modal-container/component/status-info/status-info.component';
import { MyContactsComponent } from './my-contacts/my-contacts.component';
import { MyHomePageModule } from './my-home-page/my-home-page.module';
import { NavigationModule } from './navigation/navigation.module';
import { OnboardRequestComponent } from './onboard-request/component/onboard-request/onboard-request.component';
import { ProfileService } from './profile/profile.service';
import { QuestionnaireResponseComponent } from './questionnaire-response/questionnaire-response.component';
import { SavedComponentComponent } from './saved-component/saved-component.component';
import { SavedEquityAnalysisComponent } from './saved-equity-analysis/saved-equity-analysis.component';
import { SavedSearchesModule } from './saved-searches/saved-searches.module';
import { AlertsModule } from './shared/modules/alerts/alerts.module';
import { CustomUrlSerializer } from './shared/service/customUrlSerializer/custom-url-serializer.service';
import { HttpInterceptorService } from './shared/service/httpInterceptor/http-interceptor.service';
import { LocalStorageService } from './shared/service/local-storage.service';
import { SubscriptionPlanModule } from './subscription-plan/subscription-plan.module';
import { WorkspaceModule } from "./workspace/workspace.module";
import { SaveEquityAnalysisDialogboxComponent } from './save-equity-analysis-dialogbox/save-equity-analysis-dialogbox.component';


export function appInitializer(initializerService: AppInitializer): () => Observable<void> {
  return () => initializerService.initializeApp();
}

export const options: Partial<IConfig> = {
  thousandSeparator: ',',
};

@NgModule({
  declarations: [
    AppComponent,
    ContainerPageComponent,
    DurationPipe,
    NumberFormatterPipe,
    OnboardRequestComponent,
    QuestionnaireResponseComponent,
    StatusInfoComponent,
    DiscoverClientsComponent,
    MyContactsComponent,
    AutomatedRecommendationComponent,
    MarketingComponent,
    SavedComponentComponent,
    EquityAnalysisComponent,
    SavedEquityAnalysisComponent,
    SaveEquityAnalysisDialogboxComponent,

  ],
  imports: [
    AlertsModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    BrowserModule,
    CoreModule,
    FormsModule,
    HttpClientModule,
    MatButtonModule,
    MatCheckboxModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatNativeDateModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatTabsModule,
    ReactiveFormsModule,
    SharedModule,
    SubscriptionPlanModule,
    NavigationModule,
    PktrAppMenuModule,
    DragDropModule,
    AgentLoSignupModule,
    NgxMaskModule.forRoot(options),
    ClientDashboardModule,
    SavedSearchesModule,
    CampaignsModule,
    FollowedPropertiesModule,
    DealAnalysisModule,
    WorkspaceModule,
    MyHomePageModule,
    MatCardModule,
    MatPaginatorModule,
    MatTooltipModule


  ],
  providers: [
    AppService,
    CurrencyPipe,
    DatePipe,
    DecimalPipe,
    DurationPipe,
    GTMService,
    LocalStorageService,
    NumberFormatterPipe,
    PktrAppMenuService,
    TitleCasePipe,
    TrialAccountValidatorGuard,
    UpperCasePipe,
    //  ShortNumberPipe,
    WindowService,
    {
      provide: UrlSerializer,
      useClass: CustomUrlSerializer,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpInterceptorService,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializer,
      deps: [AppInitializer],
      multi: true,
    },
  ],
  exports: [MatFormFieldModule, DragDropModule, MatInputModule, MatCheckboxModule, DurationPipe, NumberFormatterPipe],
  entryComponents: [],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(
    private appService: AppService,
    private dealAnalysisService: DealAnalysisService,
    private profileService: ProfileService
  ) { }
}
